import type { FilterSchema } from '@/shared/components/filter/validation'
import type { TFunction } from 'i18next'

import { useMemo, useState } from 'react'
import { map, paths, pipe, prop, propOr, values } from 'lodash/fp'
import { ListFilterIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'
import {
	getSalesEntitiesTree,
	toLegacyTree,
	useOptimizationGroupsQuery,
	useProductGroupCatalogQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import { useSchedulesQuery } from '@/entities/optimization-schedule'
import { useAllUsersQuery } from '@/entities/user'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import FiltersPopover from '@/shared/components/filter/FiltersPopover'
import {
	Operators,
	ValueEditorType,
	ValueType,
} from '@/shared/components/query-builder'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { StateButton } from '@/shared/components/ui/state-button'
import { OptimizationStatus } from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { toOption } from '@/shared/tools/utils'

const getSalesLevels = pipe([
	prop('sales_levels'),
	map(({ level, name }) => ({ value: String(level), label: name })),
])
export default function OptimizationGroupsFilters() {
	const { t } = useTranslation()

	const [{ filters }, setQuery] = useLocationQuery()
	const [storedKeys, setStoredKeys] = useState<string[]>([])
	const users = useAllUsersQuery()
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const productGroupsQuery = useProductGroupCatalogQuery()
	const productGroupsTree = useMemo(
		() =>
			productGroupsQuery.data
				? toLegacyTree(productGroupsQuery.data.pickerForest)
				: [],
		[productGroupsQuery.data]
	)
	const reviewers = usersOptions(users)
	const schedules = useSchedulesQuery()
	const fields = useMemo(() => getFields(t), [])
	const statuses = useMemo(() => getStatuses(t), [])
	const onApply = (filters: FilterSchema[]) => {
		setQuery({ filters: filters?.length ? filters : null })
	}
	const dataChoices = useMemo(
		() => ({
			'schedule.id': schedulesOptions(schedules),
			'group.sales_level_id': getSalesLevels(salesEntitiesQuery.data) || [],
			'group.pos_ids': getSalesEntitiesTree(salesEntitiesQuery.data) || [],
			'group.id':
				optimizationGroupsQuery?.data?.map(({ id, name }) => ({
					value: id,
					label: name,
				})) || [],
			product_groups: productGroupsTree,
			ui_status: statuses,
			reviewers,
		}),
		[
			schedules,
			salesEntitiesQuery.data,
			optimizationGroupsQuery.data,
			productGroupsTree,
			statuses,
			reviewers,
		]
	)

	return (
		<>
			<FiltersPopover
				fields={fields}
				filters={filters ?? []}
				enableReferencing={false}
				dataChoices={dataChoices}
				onApply={(rules) => {
					ampli.ogListFiltersApply()
					onApply(rules)
				}}
			>
				<StateButton
					data-testid='og-table-filters-button'
					onClear={() => onApply([])}
					active={!!filters.length}
				>
					<ListFilterIcon />
					{`${t('general_filter')}`}{' '}
					{filters.length ? `(${filters.length})` : ''}
				</StateButton>
			</FiltersPopover>
			<Separator orientation='vertical' className='h-5!' />
			{salesEntitiesQuery.isLoading || productGroupsQuery.isLoading ? (
				<div className='flex items-center space-x-2'>
					<Skeleton className='h-6 w-28' />
					<Skeleton className='h-6 w-20' />
					<Skeleton className='h-6 w-24' />
				</div>
			) : (
				<QuickFiltersBar
					irremovableKeys={['product_groups', 'group.pos_ids']}
					rules={filters}
					storedKeys={storedKeys}
					onSaveStoredKeys={setStoredKeys}
					onApply={(rules) => {
						onApply(rules.map(formatFilterRule) as FilterSchema[])
					}}
					fieldsConfig={getFiledConfig(
						fields.filter((f) =>
							['product_groups', 'group.pos_ids'].includes(f.value)
						),
						{
							'group.pos_ids':
								getSalesEntitiesTree(salesEntitiesQuery.data) || [],
							product_groups: productGroupsTree,
						}
					)}
					limit={2}
				/>
			)}
		</>
	)
}

const usersOptions = pipe([
	propOr([], 'data'),
	map(paths(['id', 'email'])),
	toOption,
])
const schedulesOptions = pipe([
	propOr({}, 'data'),
	values,
	map(paths(['id', 'name'])),
	toOption,
])
const getStatuses = (t: TFunction) =>
	[
		OptimizationStatus.New,
		OptimizationStatus.Scheduled,
		OptimizationStatus.Failed,
		OptimizationStatus.Finished,
		OptimizationStatus.Locked,
		OptimizationStatus.Pending,
	].map((status) => ({
		value: status,
		label: t(`opt_${status}`),
	}))
const getFields = (t: TFunction) => [
	{
		value: 'ui_status',
		label: t('optimization_groups_repricing_status'),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
	{
		value: 'schedule.id',
		label: t('general_schedule'),
		valueType: ValueType.array,
		valueEditorType: ValueEditorType.multiselect,
		enum: [],
	},
	{
		value: 'reviewers',
		label: t('reviewers'),
		valueType: ValueType.array,
		valueEditorType: ValueEditorType.multiselect,
		enum: [],
	},
	{
		value: 'review_completed',
		label: t('review_completed'),
		valueType: ValueType.boolean,
		valueEditorType: ValueEditorType.checkbox,
		enum: [],
	},
	{
		value: 'optimization.total_products',
		label: t('general_products'),
		valueType: ValueType.number,
		enum: [],
	},
	{
		value: 'notifications_count',
		label: t('field_schema_alerts', { ns: 'fields' }),
		valueType: ValueType.number,
		enum: [],
	},
	{
		value: 'group.sales_level_id',
		label: t('og.level'),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
	{
		value: 'group.pos_ids',
		label: t('sales_unit', { defaultValue: 'Sales unit' }),
		valueType: ValueType.array,
		valueEditorType: ValueEditorType.checkboxTree,
		operations: [Operators.ANY_OF],
		enum: [],
	},
	{
		value: 'product_groups',
		label: t('app.product_group', { defaultValue: 'Product group' }),
		valueType: ValueType.array,
		valueEditorType: ValueEditorType.checkboxTree,
		operations: [Operators.ANY_OF],
		enum: [],
	},
	{
		value: 'group.id',
		label: t('optimization_group', { defaultValue: 'Optimization group' }),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
]
